import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  User, Lock, Star, ChevronLeft, ChevronRight, 
  Loader2, Check, Send, 
  BookOpen, Calendar, GraduationCap, ArrowLeft, ShieldAlert, Trash2, CheckCircle, Quote, AlertCircle, Clock, Tag 
} from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

// --- CONFIGURAZIONE PDF ---
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ADMIN_EMAIL = 'info@tusciahub.it';

const NoteDetails = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();
  
  // --- STATI ---
  const [note, setNote] = useState(null);
  const [reviews, setReviews] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // PDF & UI
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1); 
  const [pdfDims, setPdfDims] = useState({ width: null, height: null });
  const containerRef = useRef(null);
  const [emailCopied, setEmailCopied] = useState(false);
  
  // RECENSIONI & FORM
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  
  // FEEDBACK & PERMESSI
  const [toast, setToast] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  
  // *** LIMITE PAGINE GRATIS ***
  const FREE_PAGES = 3; 

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length).toFixed(1) 
    : null;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    fetchNoteDetails();
    
    // --- CALCOLO DIMENSIONI IBRIDO (Mobile vs Desktop) ---
    const updateDims = () => {
        const isMobile = window.innerWidth < 768;
        
        if (isMobile && containerRef.current) {
            // MOBILE: Fit Width (Larghezza schermo)
            const w = containerRef.current.clientWidth; 
            setPdfDims({ width: w, height: null });
        } else {
            // DESKTOP: Fit Height (Altezza fissa, larghezza auto)
            // Impostiamo un'altezza comoda per vedere tutto il foglio senza scrollare troppo
            // Circa l'80% dell'altezza dello schermo o max 750px
            const h = Math.min(window.innerHeight * 0.8, 750); 
            setPdfDims({ width: null, height: h });
        }
    };

    updateDims();
    window.addEventListener('resize', updateDims);
    
    return () => window.removeEventListener('resize', updateDims);
  }, [noteId]);

  const fetchNoteDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      if (user && user.email === ADMIN_EMAIL) setIsAdmin(true);

      const { data: noteData, error } = await supabase
        .from('notes')
        .select('*, profiles(email)')
        .eq('id', noteId)
        .maybeSingle();

      if (error) throw error;
      if (!noteData) { setNote(null); setLoading(false); return; }

      setNote(noteData);

      if (noteData.price === 0 || (user && (user.id === noteData.user_id || user.email === ADMIN_EMAIL))) {
          setIsUnlocked(true);
      }

      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('*, profiles(email)')
        .eq('note_id', noteId)
        .order('created_at', { ascending: false });

      const visibleReviews = (reviewsData || []).filter(r => 
        r.is_approved || (user && r.user_id === user.id) || (user && user.email === ADMIN_EMAIL)
      );

      setReviews(visibleReviews);

    } catch (error) {
      console.error("Errore fetch", error);
    } finally {
      setLoading(false);
    }
  };

  const changePage = (offset) => setPageNumber(prev => Math.min(Math.max(prev + offset, 1), numPages || 1));

  const getSellerEmail = () => {
    if (!note) return "";
    if (note.profiles && note.profiles.email) return note.profiles.email;
    if (note.profiles && Array.isArray(note.profiles) && note.profiles[0]) return note.profiles[0].email;
    return "Email nascosta"; 
  };

  const handleContact = () => {
    const sellerEmail = getSellerEmail();
    if (!sellerEmail || sellerEmail.includes("nascosta")) return showToast("Email non disponibile.", "error");
    window.location.href = `mailto:${sellerEmail}?subject=${encodeURIComponent(`Info: ${note.title}`)}&body=${encodeURIComponent("Ciao, sono interessato alla tua dispensa.")}`;
  };

  const copyEmailToClipboard = () => {
    const sellerEmail = getSellerEmail();
    if(sellerEmail && !sellerEmail.includes("nascosta")) {
        navigator.clipboard.writeText(sellerEmail);
        setEmailCopied(true);
        showToast("Email copiata!", "success");
        setTimeout(() => setEmailCopied(false), 2000);
    } else {
        showToast("Impossibile copiare.", "error");
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return showToast("Accedi per recensire.", "error");
    setIsSubmittingReview(true);
    
    const tempId = Date.now();
    const optimisticReview = {
        id: tempId, created_at: new Date().toISOString(), rating: reviewForm.rating, comment: reviewForm.comment,
        user_id: currentUser.id, is_approved: false, profiles: { email: currentUser.email }, isOptimistic: true
    };

    setReviews(prev => [optimisticReview, ...prev]);
    showToast("Recensione inviata in approvazione!", "success");
    setReviewForm({ rating: 5, comment: '' });

    try {
        await supabase.from('reviews').insert([{
            subject_id: note.subject,
            note_id: note.id,
            user_id: currentUser.id,
            rating: reviewForm.rating,
            comment: reviewForm.comment,
            is_approved: false
        }]);
    } catch {
        setReviews(prev => prev.filter(r => r.id !== tempId));
        showToast("Errore invio.", "error");
    } finally {
        setIsSubmittingReview(false);
    }
  };

  const handleReviewApprove = async (reviewId) => {
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, is_approved: true } : r));
      try {
          await supabase.from('reviews').update({ is_approved: true }).eq('id', reviewId);
          showToast("Recensione approvata!", "success");
      } catch {
          showToast("Errore approvazione.", "error");
      }
  };

  const handleReviewDelete = async (reviewId) => {
      if(!window.confirm("Eliminare questa recensione?")) return;
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      try {
          await supabase.from('reviews').delete().eq('id', reviewId);
          showToast("Recensione eliminata.", "success");
      } catch {
          showToast("Errore eliminazione.", "error");
      }
  };

  const handleAdminApproveNote = async () => {
      try {
          await supabase.from('notes').update({ is_approved: true }).eq('id', note.id);
          setNote(prev => ({ ...prev, is_approved: true }));
          showToast("Dispensa Approvata!", "success");
      } catch { showToast("Errore approvazione", "error"); }
  };

  const handleAdminDeleteNote = async () => {
      if(!window.confirm("ELIMINARE definitivamente?")) return;
      try {
          if (note.file_url) {
              const fileUrlParts = note.file_url.split('/appunti/');
              if (fileUrlParts.length > 1) {
                  const filePath = fileUrlParts[1];
                  await supabase.storage.from('appunti').remove([filePath]);
              }
          }
          await supabase.from('notes').delete().eq('id', note.id);
          showToast("Eliminata con successo.", "success");
          setTimeout(() => navigate(`/subject/${note.dept_id}/${note.course_id}/${encodeURIComponent(note.subject)}`), 1000);
      } catch { showToast("Errore eliminazione", "error"); }
  };

  if (loading) return <LoadingScreen />;
  if (!note) return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-slate-400 font-bold text-xl">Dispensa non trovata.</div>;

  const isPageLocked = !isUnlocked && pageNumber > FREE_PAGES;
  const backLink = note.dept_id && note.course_id 
      ? `/subject/${note.dept_id}/${note.course_id}/${encodeURIComponent(note.subject)}`
      : `/`;

  // Skeleton dinamico
  const PageSkeleton = () => {
      const style = pdfDims.height 
        ? { height: pdfDims.height, width: pdfDims.height * 0.707 } 
        : { width: '100%', aspectRatio: '0.707' };
      
      return <div className="bg-white animate-pulse" style={style} />;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pt-24 pb-20 font-sans text-slate-900 selection:bg-indigo-200 selection:text-indigo-900">
      
      {toast && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300">
            <div className={`px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 border backdrop-blur-md min-w-[280px] ${toast.type === 'error' ? 'bg-red-500 text-white border-red-400' : 'bg-slate-900/90 text-white border-white/10'}`}>
                {toast.type === 'success' ? <CheckCircle size={20} className="text-emerald-400"/> : <AlertCircle size={20} className="text-white"/>}
                <span className="font-bold text-sm">{toast.message}</span>
            </div>
        </div>
      )}

      {/* --- HEADER --- */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mb-8 relative z-10">
        <Link to={backLink} className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 font-bold text-xs md:text-sm transition-all mb-6 bg-white/80 px-4 py-2 rounded-full border border-slate-200 shadow-sm hover:shadow-md backdrop-blur-sm">
            <ArrowLeft size={16}/> Torna a {note.subject}
        </Link>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div>
                <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4 leading-tight">{note.title}</h1>
                <div className="flex flex-wrap gap-2">
                    <Badge icon={GraduationCap} text={`Prof. ${note.professor}`} />
                    <Badge icon={Calendar} text={note.academic_year} />
                    <Badge icon={User} text={note.profiles?.email?.split('@')[0]} />
                </div>
            </div>
            
            {averageRating && (
                <div className="flex flex-col items-end animate-in fade-in zoom-in duration-500">
                    <div className="flex items-center gap-2 bg-white px-4 py-2 md:px-5 md:py-3 rounded-2xl shadow-xl shadow-indigo-100/50 border border-indigo-50">
                        <span className="text-2xl md:text-3xl font-black text-slate-900">{averageRating}</span>
                        <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill={i < Math.round(averageRating) ? "currentColor" : "none"} className={i < Math.round(averageRating) ? "" : "text-slate-200"}/>)}
                        </div>
                    </div>
                    <span className="text-[10px] md:text-xs font-bold text-slate-400 mt-2 uppercase tracking-wide mr-1">{reviews.length} Recensioni</span>
                </div>
            )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">
        
        {/* --- SINISTRA: PDF VIEWER --- */}
        {/* Usiamo w-fit su Desktop per stringere la cornice attorno al PDF */}
        <div className="lg:col-span-8 flex flex-col items-center">
            
            {/* CONTAINER PDF "TIGHT": 
                - Mobile: w-full (per adattarsi alla larghezza schermo)
                - PC: w-fit (per adattarsi alla larghezza del PDF), mx-auto (centrato)
            */}
            <div className="w-full md:w-fit mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col" ref={containerRef}>
                
                {/* TOOLBAR */}
                <div className="bg-white border-b border-slate-100 px-4 py-3 flex justify-between items-center z-20 shadow-sm shrink-0 min-w-[300px]">
                    <div className="flex items-center gap-2 text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest pl-1">
                        <BookOpen size={14} className="text-indigo-600"/> Anteprima
                    </div>
                    <div className="text-[10px] md:text-xs font-bold bg-slate-50 px-3 py-1.5 rounded-lg text-slate-600 border border-slate-200 tabular-nums">
                        Pagina {pageNumber} / {numPages || '-'}
                    </div>
                </div>

                {/* AREA DOCUMENTO */}
                <div className="relative bg-white transition-all duration-300">
                    <Document 
                        file={note.file_url} 
                        onLoadSuccess={({numPages}) => setNumPages(numPages)} 
                        loading={
                            <div className="flex flex-col items-center justify-center gap-4 text-slate-400 font-bold bg-white p-20 min-h-[400px] min-w-[300px]">
                                <Loader2 className="animate-spin text-indigo-600" size={32}/>
                                Caricamento...
                            </div>
                        } 
                    >
                        {/* WRAPPER PAGINA */}
                        <div className="relative">
                            
                            {/* OVERLAY LUCCHETTO */}
                            {isPageLocked && (
                                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center p-6 bg-white/80 backdrop-blur-sm">
                                    <div className="bg-white p-6 rounded-3xl shadow-2xl text-center max-w-xs border border-slate-200 animate-in zoom-in-95 duration-300">
                                        <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                            <Lock className="text-white" size={20} />
                                        </div>
                                        <h3 className="text-lg font-black text-slate-900 mb-2">Bloccata</h3>
                                        <p className="text-xs text-slate-500 mb-4 font-medium leading-relaxed">
                                            Limite {FREE_PAGES} pagine raggiunto.
                                        </p>
                                        <button onClick={handleContact} className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-bold text-xs hover:bg-indigo-700 transition shadow-lg shadow-indigo-200">
                                            Sblocca
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* PAGINA REALE */}
                            <Page 
                                key={`page_${pageNumber}`} 
                                pageNumber={pageNumber} 
                                width={pdfDims.width}   // Usato su Mobile
                                height={pdfDims.height} // Usato su Desktop
                                className={`transition-opacity duration-300 ${isPageLocked ? 'opacity-20 pointer-events-none' : ''}`}
                                loading={<PageSkeleton />} 
                                renderTextLayer={!isPageLocked} 
                                renderAnnotationLayer={!isPageLocked}
                            />
                        </div>
                    </Document>
                </div>

                {/* NAVIGAZIONE INFERIORE */}
                <div className="bg-white border-t border-slate-100 p-3 flex justify-between items-center gap-4 z-20 shrink-0">
                    <button 
                        disabled={pageNumber <= 1} 
                        onClick={() => changePage(-1)} 
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-xs md:text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 hover:shadow-sm active:scale-95"
                    >
                        <ChevronLeft size={16}/> Indietro
                    </button>
                    <button 
                        disabled={pageNumber >= numPages} 
                        onClick={() => changePage(1)} 
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-xs md:text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-slate-900 text-white hover:bg-indigo-600 hover:shadow-lg active:scale-95"
                    >
                        Avanti <ChevronRight size={16}/>
                    </button>
                </div>
            </div>
        </div>

        {/* --- DESTRA: SIDEBAR (4/12) --- */}
        <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            
            {isAdmin && (
                <div className="bg-slate-900 text-white p-5 rounded-3xl shadow-lg relative overflow-hidden">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 flex gap-2 relative z-10"><ShieldAlert size={12}/> Admin Dispensa</h3>
                    <div className="grid grid-cols-2 gap-2 relative z-10">
                        {!note.is_approved && <button onClick={handleAdminApproveNote} className="bg-emerald-500 hover:bg-emerald-400 text-white py-2.5 rounded-xl font-bold text-xs flex justify-center gap-1 transition">APPROVA</button>}
                        <button onClick={handleAdminDeleteNote} className={`bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl font-bold text-xs flex justify-center gap-1 transition ${note.is_approved ? 'col-span-2' : ''}`}>ELIMINA</button>
                    </div>
                </div>
            )}

            {/* BOX PREZZO & CTA */}
            <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-xl shadow-indigo-100/50 border border-slate-100 relative overflow-hidden">
                <div className="text-center mb-6 relative z-10">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Prezzo</p>
                    <h2 className="text-5xl md:text-6xl font-black text-slate-900 tracking-tighter">{note.price > 0 ? `€${Number(note.price).toFixed(2)}` : 'Gratis'}</h2>
                    
                    {note.is_negotiable && note.price > 0 && (
                        <div className="inline-flex items-center gap-1 mt-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full border border-indigo-200">
                            <Tag size={12} />
                            <span className="text-[10px] font-bold uppercase tracking-wide">Trattabile</span>
                        </div>
                    )}
                </div>

                <div className="space-y-4 relative z-10">
                    <button onClick={handleContact} className="w-full bg-slate-900 text-white py-4 rounded-2xl font-bold text-lg hover:bg-indigo-600 transition shadow-xl hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-2">
                        Contatta Venditore
                    </button>
                    <div onClick={copyEmailToClipboard} className="text-center cursor-pointer group">
                        <span className="text-xs font-bold text-slate-400 group-hover:text-indigo-600 transition-colors border-b border-transparent group-hover:border-indigo-200">
                            {getSellerEmail()} (Clicca per copiare)
                        </span>
                    </div>
                </div>
            </div>

            {/* DESCRIZIONE */}
            <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Quote className="text-indigo-400 fill-indigo-50" size={20}/> Descrizione</h3>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{note.description || "Nessuna descrizione disponibile."}</p>
            </div>

            {/* RECENSIONI */}
            <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-lg text-slate-900 pl-2">Recensioni</h3>
                </div>

                <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {reviews.length > 0 ? reviews.map(r => (
                        <div key={r.id} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 relative transition hover:bg-white hover:shadow-md">
                             {(r.isOptimistic || !r.is_approved) && (
                                <span className="absolute top-3 right-3 flex items-center gap-1 bg-amber-100 text-amber-700 text-[9px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                                    <Clock size={10}/> In approvazione
                                </span>
                             )}
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-black">
                                    {r.profiles?.email?.charAt(0).toUpperCase() || "U"}
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-slate-900">{r.profiles?.email.split('@')[0]}</div>
                                    <div className="flex text-yellow-400 gap-0.5 mt-0.5">
                                        {[...Array(5)].map((_, i) => <Star key={i} size={8} fill={i < r.rating ? "currentColor" : "none"} className={i < r.rating ? "" : "text-slate-200"}/>)}
                                    </div>
                                </div>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed pl-1">{r.comment}</p>
                            {isAdmin && (
                                <div className="flex gap-2 mt-3 pt-2 border-t border-slate-200/50 justify-end">
                                    {!r.is_approved && <button onClick={() => handleReviewApprove(r.id)} className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700 p-1.5 rounded-lg transition"><Check size={12} strokeWidth={3}/></button>}
                                    <button onClick={() => handleReviewDelete(r.id)} className="bg-rose-100 hover:bg-rose-200 text-rose-700 p-1.5 rounded-lg transition"><Trash2 size={12} strokeWidth={3}/></button>
                                </div>
                            )}
                        </div>
                    )) : (
                        <div className="text-center py-6 border-2 border-dashed border-slate-100 rounded-2xl">
                            <p className="text-xs font-bold text-slate-400">Nessuna recensione</p>
                        </div>
                    )}
                </div>

                <div className="bg-slate-50 p-1 rounded-2xl border border-slate-100">
                    <form onSubmit={handleReviewSubmit}>
                        <div className="flex justify-center gap-2 py-3">
                             {[1, 2, 3, 4, 5].map((star) => (
                                <button key={star} type="button" onClick={() => setReviewForm({...reviewForm, rating: star})} className="hover:scale-110 transition focus:outline-none">
                                    <Star size={20} fill={star <= reviewForm.rating ? "#FBBF24" : "none"} className={star <= reviewForm.rating ? "text-yellow-400 drop-shadow-sm" : "text-slate-200"} />
                                </button>
                            ))}
                        </div>
                        <div className="relative">
                            <input type="text" required placeholder="Scrivi la tua opinione..." className="w-full text-xs p-4 pr-12 rounded-xl border-none focus:ring-0 bg-white shadow-sm h-12" value={reviewForm.comment} onChange={e => setReviewForm({...reviewForm, comment: e.target.value})}/>
                            <button type="submit" disabled={isSubmittingReview} className="absolute right-2 top-2 bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 shadow-md">
                                {isSubmittingReview ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </div>
      </div>
      
      <style jsx>{` .custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; } `}</style>
    </div>
  );
};

const Badge = ({ icon: Icon, text }) => (<span className="bg-white/60 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 flex items-center gap-2 shadow-sm"><Icon size={12} className="text-indigo-500"/> {text}</span>);

export default NoteDetails;